import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { CLEAN_DNI_FORMAT, CURRENCY_DOTS_FORMAT, ONLY_NUMBERS_FORMAT } from './regexes'

/**
 * To clean a value that is formatted as a Chilean DNI.
 * Returns the given value without points and hyphen.
 */
export const unFormatDNI = value => (typeof value === 'string' ? value.replace(CLEAN_DNI_FORMAT, '').toUpperCase() : '')

/**
 * To format a given value to a currency format. By default,
 * we use 0 decimal places. If the symbol is not passed as a
 * parameter, then the formatted value doesn't include this.
 * The decimal part means that at most the first X decimal
 * places will be displayed. The symbol would be one of:
 *    - $, US$, €, UF, UTM
 * The symbol and decimal places normally can be obtained from the DB.
 */
export const formatCurrency = (currency, symbol = undefined, decimals = 0) => {
  const [integer, fraction] = currency.toString().split('.')
  let updatedCurrency = integer.replace(CURRENCY_DOTS_FORMAT, '$1.')

  if (symbol) {
    updatedCurrency = `${symbol} ${updatedCurrency}`
  }

  if ((!fraction && fraction !== 0) || decimals === 0) {
    return updatedCurrency
  }

  return `${updatedCurrency},${fraction.substring(0, decimals)}`
}

/**
 * To format the given value by the date format pattern.
 */
export const formatDate = (value, pattern = 'dd/MM/yyyy HH:mm') => format(new Date(value), pattern, { locale: es })

/**
 * To format the given value to a chilean DNI format.
 * The type parameter can have the values 1 or 2.
 * Type 1 means the given value is formatted as 11.111.111-1.
 * Type 2 means the given value is formatted as 11111111-1.
 */
export const formatDNI = (value, type = 1) => {
  const updatedValue = unFormatDNI(value?.toString())

  if (updatedValue.length === 0) return updatedValue
  if (updatedValue.length === 1) return updatedValue

  const verifiedDigit = updatedValue.substring(updatedValue.length - 1)
  const identifier = updatedValue.substring(0, updatedValue.length - 1)

  return type === 1
    ? `${identifier.replace(CURRENCY_DOTS_FORMAT, '$1.')}-${verifiedDigit}`
    : `${identifier}-${verifiedDigit}`
}

/**
 * To format the given value to only numbers.
 */
export const formatOnlyNumbers = value => value.replace(ONLY_NUMBERS_FORMAT, '')

/**
 * To format the given value to a percentage format.
 */
export const formatPercentage = (value, decimals = 2) => {
  if (typeof value !== 'number' && typeof value !== 'string') {
    return value
  }

  return `${parseFloat(value).toFixed(decimals)}%`
}

/**
 * To format by the given regular expression.
 */
export const formatRegEx = (value, regex) => {
  if (typeof value !== 'string') {
    return value
  }

  return value.replace(regex, '')
}
